// frontend-react/src/pages/UserPermissionPage.tsx
import React, { useState, useEffect } from 'react';
import { Form, Switch, Button, message, Spin, Card } from 'antd';
import {
  ArrowLeftOutlined,
  UploadOutlined,
  ExperimentOutlined,
  BulbOutlined,
  DiffOutlined,
  AuditOutlined,
  QuestionCircleOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { getApiBaseUrl } from '../config/api';
import { authService } from '../services/authService';
import { useFeatureFlags } from '../hooks/useFeatureFlags';

interface UserPermissions {
  can_use_ad_review: boolean;
  can_use_contract_comparison: boolean;
  can_upload_rules: boolean;
  can_edit_rules: boolean;
  can_use_ai_rules: boolean;
  can_use_validation_mode: boolean;
  can_edit_review_cards: boolean;
  can_manage_users: boolean;
  can_edit_faq: boolean;
}

interface UserInfo {
  user_id: number;
  name: string;
  email: string;
  department?: string;
  roles: string[];
  is_department_manager: boolean;
  permissions: UserPermissions;
}

/** roles 陣列中是否含有指定角色（避免 tsconfig lib 不含 es2016） */
const hasRole = (roles: string[] | undefined, role: string) =>
  !!roles && roles.indexOf(role) >= 0;

const isAdminRole = (roles: string[] | undefined) =>
  hasRole(roles, 'ADMIN') || hasRole(roles, 'SUPER_ADMIN');

// ─── 設計 token ──────────────────────────────────────────────────
const C = {
  border:  'var(--color-border)',
  card:    'var(--color-card)',
  surface: 'var(--color-surface)',
  text:    'var(--color-text)',
  muted:   '#9ca3af',
  primary: '#1e88a8',
  radius:  'var(--radius)',
};

// ─── 小元件 ──────────────────────────────────────────────────────

/** 區段標題 */
const Sec: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <div style={{
    display: 'flex', alignItems: 'center',
    fontSize: '14px', fontWeight: 700,
    color: C.text,
    paddingLeft: '10px',
    borderLeft: `3px solid ${C.primary}`,
    marginBottom: '12px', ...style,
  }}>
    {children}
  </div>
);

/** 功能橫列（icon + 標題說明 + switch） */
const FRow: React.FC<{
  name: string;
  icon: React.ReactNode;
  color: string;
  label: string;
  desc: React.ReactNode;
  top?: boolean;
}> = ({ name, icon, color, label, desc, top }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '14px',
    padding: '14px 18px',
    borderTop: top ? `1px solid ${C.border}` : undefined,
  }}>
    <div style={{
      width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
      background: color + '1a', color, fontSize: '16px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {icon}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: '14px', fontWeight: 600, color: C.text, lineHeight: 1.3 }}>{label}</div>
      <div style={{ fontSize: '12px', color: C.muted, marginTop: '3px' }}>{desc}</div>
    </div>
    <Form.Item name={name} valuePropName='checked' style={{ margin: '0 0 0 16px', flexShrink: 0 }}>
      <Switch />
    </Form.Item>
  </div>
);

/** 全文審閱子功能格 */
const Sub: React.FC<{ name: string; icon: React.ReactNode; label: string; desc: string }> = ({ name, icon, label, desc }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '12px 14px',
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: '8px',
  }}>
    <div style={{
      width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
      background: '#e6f4f8', color: C.primary, fontSize: '13px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {icon}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: '14px', fontWeight: 600, color: C.text, lineHeight: 1.3 }}>{label}</div>
      <div style={{ fontSize: '12px', color: C.muted, marginTop: '3px' }}>{desc}</div>
    </div>
    <Form.Item name={name} valuePropName='checked' style={{ margin: 0, flexShrink: 0 }}>
      <Switch size='small' />
    </Form.Item>
  </div>
);

// ─── 主元件 ──────────────────────────────────────────────────────

const UserPermissionPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const token = authService.getToken();
  const {
    faqEnabled, adReviewEnabled,
    aiRulesEnabled, aiRuleOptimizationEnabled, contractComparisonEnabled,
  } = useFeatureFlags();
  const adReviewOn = Form.useWatch('can_use_ad_review', form);

  useEffect(() => {
    if (userId) fetchUserPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchUserPermissions = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/v1/user/${userId}/permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('無法獲取使用者權限');
      const data = await res.json();
      setUserInfo(data);
      form.setFieldsValue(data.permissions);
    } catch (e: any) {
      message.error(e.message || '載入權限資料失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoSave = async (allValues: UserPermissions) => {
    setSaving(true);
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/v1/user/${userId}/permissions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(allValues),
      });
      if (!res.ok) throw new Error('更新權限失敗');
      message.success('已儲存', 1);
      // 若修改的是當前登入者自己的權限，靜默刷新
      const currentUser = authService.getUser();
      if (currentUser && currentUser.id === userId) {
        await authService.refreshCurrentUser();
      }
    } catch (e: any) {
      message.error(e.message || '更新權限失敗');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !userInfo) {
    return (
      <PageLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Spin size='large' />
        </div>
      </PageLayout>
    );
  }

  const panel: React.CSSProperties = {
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: C.radius,
    overflow: 'hidden',
  };

  return (
    <PageLayout>
      <div style={{ padding: '24px 32px', maxWidth: '1060px', margin: '0 auto', zoom: 1.1 }}>

        {/* 頁首：返回 + 標題 + 儲存狀態 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/user-management')}>
            返回
          </Button>
          <div style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: 'var(--color-text)' }}>
            權限設定
          </div>
          {saving && (
            <span style={{ fontSize: '12px', color: C.muted }}>儲存中…</span>
          )}
        </div>

        {/* 主體兩欄 */}
        <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '20px', alignItems: 'stretch' }}>

          {/* ━━ 左欄：使用者資訊 ━━━━━━━━━━━━━━━━━━━ */}
          <Card styles={{ body: { padding: '24px 20px', height: '100%', boxSizing: 'border-box' } }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                background: C.primary, color: '#fff',
                fontSize: '22px', fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {userInfo?.name?.charAt(0) ?? <UserOutlined />}
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '16px', fontWeight: 700, color: C.text }}>
                  {userInfo?.name ?? '—'}
                </div>
                {/* 角色標籤 */}
                {userInfo && (
                  <div style={{
                    display: 'inline-block', marginTop: '6px',
                    fontSize: '11px', fontWeight: 600, lineHeight: 1,
                    padding: '3px 8px', borderRadius: '4px',
                    background: isAdminRole(userInfo.roles)
                      ? '#fef3c7' : userInfo.is_department_manager ? '#dbeafe' : '#f3f4f6',
                    color: isAdminRole(userInfo.roles)
                      ? '#92400e' : userInfo.is_department_manager ? '#1e40af' : '#6b7280',
                  }}>
                    {hasRole(userInfo.roles, 'SUPER_ADMIN') ? '平台管理員'
                      : hasRole(userInfo.roles, 'ADMIN') ? 'Admin'
                      : userInfo.is_department_manager ? '部門主管'
                      : '一般使用者'}
                  </div>
                )}
                <div style={{ fontSize: '12px', color: C.muted, marginTop: '6px', wordBreak: 'break-all', lineHeight: 1.4 }}>
                  {userInfo?.email ?? '—'}
                </div>
                {userInfo?.department && (
                  <div style={{ fontSize: '12px', color: C.text, marginTop: '4px' }}>
                    {userInfo.department}
                  </div>
                )}
              </div>
            </div>

            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '14px' }}>
              <div style={{ fontSize: '11px', color: '#4b5563', fontWeight: 700, letterSpacing: '0.05em', marginBottom: '8px' }}>
                資料存取範圍
              </div>
              {(() => {
                const isAdmin = isAdminRole(userInfo?.roles);
                const isManager = userInfo?.is_department_manager;
                const label = isAdmin ? '全公司' : isManager ? '部門內' : '僅限個人';
                const desc = isAdmin
                  ? '可查看全公司所有使用者的資料與審閱紀錄'
                  : isManager
                  ? '可查看部門內所有成員的資料與審閱紀錄'
                  : '僅可查看自己的資料與審閱紀錄';
                const bg = isAdmin ? '#fef3c7' : isManager ? '#dbeafe' : C.surface;
                const accent = isAdmin ? '#92400e' : isManager ? '#1e40af' : '#374151';
                return (
                  <div style={{
                    background: bg, borderRadius: '8px',
                    padding: '10px 12px',
                  }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: accent, lineHeight: 1.3 }}>
                      {label}
                    </div>
                    <div style={{ fontSize: '11px', color: '#4b5563', marginTop: '3px', lineHeight: 1.5 }}>
                      {desc}
                    </div>
                  </div>
                );
              })()}
            </div>
          </Card>

          {/* ━━ 右欄：權限設定 ━━━━━━━━━━━━━━━━━━━━ */}
          <Card styles={{ body: { padding: '28px 32px' } }}>
            <Form
              form={form}
              onValuesChange={(_, allValues) => handleAutoSave(allValues as UserPermissions)}
              initialValues={{
                can_use_ad_review: true,
                can_use_contract_comparison: false,
                can_upload_rules: false,
                can_edit_rules: false,
                can_manage_users: false,
                can_edit_faq: false,
                can_use_ai_rules: false,
                can_use_validation_mode: false,
                can_edit_review_cards: false,
              }}
            >

              {/* ── AI 功能 ── */}
              <Sec>AI 功能</Sec>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>

                {contractComparisonEnabled && (
                  <div style={panel}>
                    <FRow
                      name='can_use_contract_comparison'
                      icon={<DiffOutlined />} color='#0d9488'
                      label='合約比對'
                      desc='定型化契約比對、合約版本比對'
                    />
                  </div>
                )}

                {adReviewEnabled && (
                  <div style={panel}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '14px',
                      padding: '14px 18px',
                      borderBottom: adReviewOn ? `1px solid ${C.border}` : undefined,
                    }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                        background: '#1e88a81a', color: C.primary, fontSize: '16px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <AuditOutlined />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: C.text, lineHeight: 1.3 }}>全文審閱</div>
                        <div style={{ fontSize: '12px', color: C.muted, marginTop: '3px' }}>
                          允許此使用者使用全文審閱功能
                        </div>
                      </div>
                      <Form.Item name='can_use_ad_review' valuePropName='checked' style={{ margin: '0 0 0 16px', flexShrink: 0 }}>
                        <Switch />
                      </Form.Item>
                    </div>

                    {adReviewOn ? (
                      <div style={{ padding: '12px 16px 10px', background: C.surface }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                          <Sub name='can_upload_rules'        icon={<UploadOutlined />}     label='上傳規則'   desc='上傳自訂規則檔案' />
                          {aiRulesEnabled && (
                            <Sub name='can_use_ai_rules'      icon='✨'                     label='AI 審閱'    desc='AI 自動生成，不需撰寫規則' />
                          )}
                          <Sub name='can_use_validation_mode' icon={<ExperimentOutlined />} label='驗測模式'   desc='自行開啟驗測模式' />
                          {aiRuleOptimizationEnabled && (
                            <Sub name='can_edit_review_cards' icon={<BulbOutlined />}       label='AI 規則優化' desc='AI 偵測卡片修改，自動優化規則' />
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>

              {/* ── 管理功能 ── */}
              <Sec style={{ marginTop: '20px' }}>管理功能</Sec>
              <div style={panel}>
                <FRow
                  name='can_manage_users'
                  icon={<TeamOutlined />} color='#ef4444'
                  label='使用者管理'
                  desc={<>可使用使用者管理頁面的所有權限，<span style={{ color: '#ef4444' }}>請謹慎開啟</span></>}
                />
                {faqEnabled && (
                  <FRow
                    name='can_edit_faq'
                    icon={<QuestionCircleOutlined />} color='#d97706'
                    label='常見問答'
                    desc='新增、編輯、刪除常見問答內容'
                    top
                  />
                )}
              </div>

            </Form>
          </Card>

        </div>
      </div>
    </PageLayout>
  );
};

export default UserPermissionPage;
